/**
 * What a named squadron actually had on strength: its front-line aeroplane, the
 * mix of novices, regulars and veterans in its cockpits, and which of the
 * twenty-five famous aces were with it.
 *
 * **This is decoded, not inferred.** `ELITE.DAT` and `ORDINARY.DAT` were open
 * questions until this lane: docs/formats/missions-campaign.md carried them as
 * *"INFERRED: `(squadron × month × pilot quality) → aircraftCode` tables ... the
 * stride is solvable algebraically. **Not yet done.**"* It is done now, and the
 * layout is exact:
 *
 * ```text
 * file  := record[N] , namePool          ELITE.DAT N=10, ORDINARY.DAT N=24
 * record:= header(8) , month(8)[36]      296 bytes, no padding
 * header:= FE , elite:u8 , nameOffset:u16le , nation:u8 , service:u8 , prestige:u8 , FF
 * month := aerodrome:u8 , ace:u8 , acNovice:u8 , acRegular:u8 , acVeteran:u8 ,
 *          pctNovice:u8 , pctRegular:u8 , pctVeteran:u8      all FF = not on strength
 * ```
 *
 * Every one of the five claims above is checked, not assumed:
 *
 * - **`elite` (header byte 1) is 1 in every `ELITE.DAT` record and 0 in every
 *   `ORDINARY.DAT` record.** The elite/ordinary split is stamped in the data,
 *   not merely implied by which file a unit sits in.
 * - **`nameOffset` chains perfectly.** `No.24 Squadron` at 0, `No.56 Squadron`
 *   at 15 = `len+1`, and so on through all 34 without a gap.
 * - **The three percentages sum to exactly 100 in all 645 populated monthly
 *   rows, with zero violations.** That is what identifies them as a pilot-quality
 *   mix rather than three unrelated bytes.
 * - **`ace` indexes `FAMOUS_ACES`** (`mission-roster.ts`, `ACE.DAT` order). It
 *   resolves Hawker onto No.24 Squadron, Boelcke onto Jasta 2, Richthofen onto
 *   Jasta 11, Collishaw onto the Black Flight and Rickenbacker onto the 94th
 *   Aero — five independent historical facts the byte was never told.
 * - **`acVeteran` indexes `PS.EXE`'s own 33-name aircraft table** (the
 *   `morane`/`airco`/…/`junker` run at file offset 256230). It puts No.24
 *   Squadron on the D.H.2, No.56 on the S.E.5a and Jasta 11 on the Albatros
 *   D.III — again historically right, and again not something a mis-read byte
 *   would land on three times.
 *
 * **The open question this settles.** docs/game/aces-squadrons.md §4 asks
 * *"Elite vs. ordinary: are elite-squadron AI pilots actually more skilled?"* and
 * answers *"**Not confirmed directly.**"* It is confirmed now, and the answer is
 * yes, materially. Across every populated month the two files draw from one
 * six-rung ladder and sit on different parts of it:
 *
 * | | veteran share | mixes used | mean veteran |
 * |---|---|---|---|
 * | `ELITE.DAT` | 35–90% | `0/10/90`, `10/20/70`, `20/30/50`, `25/40/35` | 59.0% |
 * | `ORDINARY.DAT` | 20–50% | `20/30/50`, `25/40/35`, `35/45/20`, `40/40/20` | 36.2% |
 *
 * They overlap on the middle two rungs and nowhere else. So an elite squadron is
 * not a re-skinned ordinary one: it fields roughly five veterans for every three
 * an ordinary unit puts up, and the two best rungs in the game — J.G.1's and
 * No.74 Squadron's `0/10/90` — are elite-only.
 *
 * **Bucketing is port-side, and it is the only port-side step here.** The file
 * is monthly, 36 rows covering December 1915 to November 1918 — exactly the
 * career calendar (`calendar.ts`). A single mission has no calendar, only an
 * era (`mission-roster.ts`'s own note), so each squadron's months are collapsed
 * into the three eras this port uses by taking the **modal** row of each bucket:
 * `early` = months 0–9 (Dec 1915 – Sep 1916), `mid` = 10–24 (Oct 1916 – Dec
 * 1917), `late` = 25–35 (Jan – Nov 1918). An era a squadron flew no month in is
 * simply absent, which is how the Black Flight stays a mid-war unit and No.74
 * Squadron a 1918 one without a hand-written date needing to say so.
 *
 * The three fields this table drops are recorded rather than silently discarded:
 * the per-month **aerodrome** (header-relative byte 0 — an `AERO.DAT` index; it
 * resolves Bertangles for No.24, Douai for Jasta 11 and Cambrai for Jasta 2, but
 * `aerodromes.ts` carries 37 of `AERO.DAT`'s 40 fields so the high indices do
 * not line up and `Squadron.aerodrome` is used instead), the **novice and
 * regular** aircraft codes (a squadron re-equips by seniority — veterans first,
 * then regulars, then novices — which this port has nowhere to show), and the
 * header's **prestige** byte (5–35 for elite units, 0–15 for ordinary, J.G.1
 * highest at 35: the career transfer gate behind the manual's *"you may attain
 * enough clout to fly with J.G.1"*, and a lead for the campaign lane rather
 * than for a single mission).
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import type { AiSkill } from "../combat/skill.ts";
import type { MissionDraw } from "../mission/mission-build.ts";
import type { MissionEra } from "../mission/mission-world.ts";

/** One squadron's strength in one era, straight off the decoded monthly rows. */
export interface SquadronStrength {
  /** What its veterans flew — the unit's front-line type that era. */
  readonly aircraft: AircraftKey;
  /** Percentage of the cockpits held by each tier. Sums to 100, as the file's own rows do. */
  readonly novicePct: number;
  readonly regularPct: number;
  readonly veteranPct: number;
  /** `FAMOUS_ACES` ids on strength that era, in the order the months name them. */
  readonly aces: readonly string[];
}

function era(
  aircraft: AircraftKey,
  novicePct: number,
  regularPct: number,
  veteranPct: number,
  aces: readonly string[] = [],
): SquadronStrength {
  return { aircraft, novicePct, regularPct, veteranPct, aces };
}

/**
 * Every named squadron's decoded strength, by era. A `Squadron` id absent from
 * an era flew no month in it and cannot be met there.
 */
export const SQUADRON_STRENGTH: Readonly<
  Record<string, Partial<Record<MissionEra, SquadronStrength>>>
> = {
  "sqn-24": {
    early: era("AIRCO", 25, 40, 35, ["hawker"]),
    mid: era("AIRCO", 25, 40, 35, ["hawker"]),
    late: era("SE5", 20, 30, 50),
  },
  "sqn-56": {
    mid: era("SE5", 10, 20, 70),
    late: era("SE5", 10, 20, 70),
  },
  "sqn-74": {
    late: era("SE5", 0, 10, 90),
  },
  "black-flight": {
    mid: era("SOPTRI", 10, 20, 70, ["collishaw"]),
  },
  storks: {
    early: era("NIEUPORT", 25, 40, 35),
    mid: era("SPAD_7", 20, 30, 50),
    late: era("SPAD_13", 10, 20, 70),
  },
  "aero-94": {
    late: era("SPAD_13", 10, 20, 70, ["rickenbacker"]),
  },
  "flabt-203": {
    early: era("FOKE3", 20, 30, 50, ["boelcke"]),
  },
  "jasta-2": {
    mid: era("ALBA_D2", 20, 30, 50, ["boelcke"]),
  },
  "jasta-11": {
    mid: era("ALBA_D3", 10, 20, 70, ["richthofen"]),
  },
  "jg-1": {
    mid: era("FOKDR1", 0, 10, 90, ["lothar", "richthofen"]),
    late: era("FOKD7", 0, 10, 90, ["richthofen", "goering"]),
  },
  "sqn-3": {
    early: era("MORANE", 35, 45, 20),
    mid: era("NIEUPORT", 25, 40, 35),
    late: era("SE5", 20, 30, 50),
  },
  "sqn-11": {
    early: era("MORANE", 20, 30, 50),
  },
  "sqn-40": {
    mid: era("NIEUPORT", 25, 40, 35),
    late: era("SE5", 20, 30, 50),
  },
  "sqn-60": {
    early: era("MORANE", 35, 45, 20),
    mid: era("NIEUPORT", 25, 40, 35),
    late: era("SE5", 20, 30, 50),
  },
  "sqn-85": {
    late: era("SE5", 20, 30, 50, ["bishop"]),
  },
  "hd-39": {
    early: era("SOPPUP", 35, 45, 20),
    mid: era("SOPPUP", 35, 45, 20),
    late: era("CAMEL", 35, 45, 20),
  },
  "hd-61": {
    mid: era("SOPPUP", 25, 40, 35),
    late: era("CAMEL", 25, 40, 35),
  },
  "hd-143": {
    late: era("SE5", 20, 30, 50),
  },
  "naval-3": {
    early: era("MORANE", 35, 45, 20),
    mid: era("SOPTRI", 25, 40, 35),
    late: era("CAMEL", 20, 30, 50, ["collishaw"]),
  },
  "naval-8": {
    mid: era("SOPPUP", 25, 40, 35),
    late: era("CAMEL", 20, 30, 50),
  },
  "esc-23": {
    early: era("MORANE", 35, 45, 20),
    mid: era("SPAD_7", 25, 40, 35),
    late: era("SPAD_13", 20, 30, 50),
  },
  "esc-38": {
    early: era("MORANE", 35, 45, 20),
    mid: era("SPAD_7", 25, 40, 35),
    late: era("SPAD_13", 20, 30, 50),
  },
  "aero-27": {
    late: era("SPAD_13", 20, 30, 50),
  },
  "flabt-62": {
    early: era("FOKE3", 35, 45, 20),
  },
  "kek-2": {
    early: era("FOKE3", 35, 45, 20),
  },
  "kek-habsheim": {
    early: era("FOKE3", 35, 45, 20),
    mid: era("FOKE3", 35, 45, 20),
  },
  "fokker-staffel-3": {
    early: era("FOKE3", 35, 45, 20),
    mid: era("FOKE3", 35, 45, 20),
  },
  "jasta-5": {
    mid: era("ALBA_D5", 25, 40, 35),
    late: era("FOKD7", 20, 30, 50),
  },
  "jasta-9": {
    mid: era("ALBA_D2", 25, 40, 35),
    late: era("FOKD7", 20, 30, 50),
  },
  "jasta-14": {
    mid: era("ALBA_D2", 25, 40, 35),
    late: era("FOKD7", 20, 30, 50),
  },
  "jasta-15": {
    mid: era("ALBA_D5", 25, 40, 35),
    late: era("FOKD7", 20, 30, 50),
  },
  "jasta-24": {
    mid: era("ALBA_D5", 25, 40, 35),
    late: era("FOKD7", 20, 30, 50),
  },
  "jasta-27": {
    mid: era("ALBA_D5", 25, 40, 35, ["goering"]),
    late: era("FOKD7", 20, 30, 50, ["goering"]),
  },
  "jasta-37": {
    mid: era("ALBA_D5", 25, 40, 35, ["udet"]),
    late: era("FOKD7", 20, 30, 50, ["udet"]),
  },
};

export function squadronStrength(id: string, missionEra: MissionEra): SquadronStrength | undefined {
  return SQUADRON_STRENGTH[id]?.[missionEra];
}

/**
 * One cockpit's pilot, drawn from the unit's own decoded percentages. The three
 * add to 100 in the file, so the draw is a plain roll over that hundred rather
 * than a normalisation of three numbers that might not have agreed.
 */
export function drawPilotSkill(draw: MissionDraw, strength: SquadronStrength): AiSkill {
  const roll = draw.int(1, 100);
  if (roll <= strength.novicePct) return "novice";
  return roll <= strength.novicePct + strength.regularPct ? "regular" : "veteran";
}
